import React from 'react';
import Icon from '../../../components/AppIcon';

// ── Brand colours ────────────────────────────────────────────────────────────
export const BLUE         = '#004AAD';
export const BLUE_SEC     = '#0082FB';
export const BLUE_GRAD    = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
export const BLUE_GRAD_B  = 'linear-gradient(180deg, #090A0B 0%, #001a4d 30%, #004AAD 70%, #0082FB 100%)';
export const BLUE_SOFT    = 'rgba(0,74,173,0.07)';
export const BLUE_SHADOW  = 'rgba(0,74,173,0.28)';

// ── Typography token ─────────────────────────────────────────────────────────
// display → Sansation for headings, titles, labels, badges, buttons
// all others → inherit
export const F = {
  display : "'Sansation', 'Plus Jakarta Sans', system-ui, sans-serif",
  body    : 'inherit',
  mono    : 'inherit',
};

// ── Ring color system — single source of truth ───────────────────────────────
// Thresholds: 85 / 70 / 50 — used everywhere
const getRingColor = (n) => {
  if (n >= 85) return '#10F090';
  if (n >= 70) return '#22D3EE';
  if (n >= 50) return '#F59E0B';
  return '#B5325B';
};

// ── Colour helpers ────────────────────────────────────────────────────────────
export const getScoreColor = (score) => {
  const n = Number(score) || 0;
  if (n >= 85) return { text: '#10F090', bar: '#10F090', bg: 'rgba(16,240,144,0.10)',  border: 'rgba(16,240,144,0.30)'  };
  if (n >= 70) return { text: '#22D3EE', bar: '#22D3EE', bg: 'rgba(34,211,238,0.10)',  border: 'rgba(34,211,238,0.30)'  };
  if (n >= 50) return { text: '#F59E0B', bar: '#F59E0B', bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.30)'  };
  return             { text: '#B5325B', bar: '#B5325B', bg: 'rgba(255,87,51,0.10)',   border: 'rgba(255,87,51,0.30)'   };
};

export const getGrade = (score) => {
  const n = Number(score) || 0;
  if (n >= 85) return { label: 'Excellent',        bg: 'rgba(16,240,144,0.12)',  color: '#10F090' };
  if (n >= 70) return { label: 'Good',              bg: 'rgba(34,211,238,0.12)',  color: '#22D3EE' };
  if (n >= 50) return { label: 'Fair · Needs Work', bg: 'rgba(245,158,11,0.12)', color: '#F59E0B' };
  return             { label: 'Critical',           bg: 'rgba(255,87,51,0.12)',  color: '#B5325B' };
};

export const getScoreNum = (s) => {
  const m = String(s || '').match(/\d+/);
  return m ? Math.min(parseInt(m[0]), 100) : 0;
};

export const fmtScore = (s) => {
  const n = getScoreNum(s);
  return n ? `${n}/100` : (s || 'N/A');
};

// ── ScoreCircle ───────────────────────────────────────────────────────────────
export const ScoreCircle = ({ score, size = 160 }) => {
  const n      = Number(score) || 0;
  const r      = size * 0.42;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (n / 100);
  const grade  = getGrade(n);
  const color  = getRingColor(n);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
          style={{ transform: 'rotate(-90deg)' }}>
          {/* Track */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none" stroke="rgba(0,0,0,0.05)" strokeWidth="10"
          />
          {/* Filled arc — score-based ring color */}
          <circle
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeDasharray={`${filled} ${circ - filled}`}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dasharray 1s ease',
              filter: `drop-shadow(0 0 6px ${color}60)`,
            }}
          />
        </svg>

        {/* Centre text */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 2,
        }}>
          {/* Score number — Sansation */}
          <span style={{
            fontSize: size * 0.28, fontWeight: 800,
            fontFamily: F.display,
            color: color, lineHeight: 1,
            textShadow: `0 0 20px ${color}50`,
          }}>
            {n}
          </span>
          {/* "of 100" — inherit */}
          <span style={{
            fontSize: size * 0.08, fontWeight: 500,
            fontFamily: 'inherit',
            color: '#94a3b8',
          }}>
            of 100
          </span>
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        {/* "Overall Score" — Sansation */}
        <p style={{
          fontFamily: F.display, fontWeight: 700, fontSize: 15,
          color: '#0f172a', marginBottom: 6,
          letterSpacing: '-0.02em',
        }}>
          Overall Score
        </p>
        {/* Grade badge — Sansation */}
        <span style={{
          background: grade.bg, color: grade.color,
          borderRadius: 20, padding: '4px 16px',
          fontSize: 12, fontWeight: 700,
          fontFamily: F.display,
          border: `1px solid ${grade.color}40`,
        }}>
          {grade.label}
        </span>
      </div>
    </div>
  );
};

// ── EmptyState ────────────────────────────────────────────────────────────────
export const EmptyState = ({
  icon  = 'FileText',
  title = 'No data available',
  desc  = 'This section will populate from your HTML report.',
}) => (
  <div style={{
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '64px 0', textAlign: 'center',
  }}>
    <div style={{
      width: 64, height: 64, borderRadius: 16,
      background: BLUE_SOFT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 16,
    }}>
      <Icon name={icon} size={28} color={BLUE} />
    </div>

    {/* Title — Sansation */}
    <h3 style={{
      fontFamily: F.display, fontSize: 15, fontWeight: 700,
      letterSpacing: '-0.02em', color: '#0f172a', marginBottom: 8,
    }}>
      {title}
    </h3>

    {/* Description — inherit */}
    <p style={{
      fontFamily: 'inherit', fontSize: 13, fontWeight: 400,
      color: '#94a3b8', maxWidth: 360, lineHeight: 1.6, margin: 0,
    }}>
      {desc}
    </p>
  </div>
);

// ── SectionCard ───────────────────────────────────────────────────────────────
export const SectionCard = ({ title, action, children }) => (
  <div style={{
    background: '#ffffff',
    borderRadius: 20,
    border: '0.5px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  }}>
    <div style={{
      padding: '16px 24px',
      borderBottom: '0.5px solid rgba(0,0,0,0.06)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderLeft: '3px solid transparent',
      borderImage: 'linear-gradient(180deg, #090A0B, #001a4d, #004AAD, #0082FB) 1',
    }}>
      {/* Title — Sansation */}
      <h2 style={{
        fontFamily: F.display,
        fontSize: 18, fontWeight: 700,
        letterSpacing: '-0.025em',
        color: '#0f172a', margin: 0,
      }}>
        {title}
      </h2>
      {action}
    </div>
    <div style={{ padding: '20px 24px' }}>
      {children}
    </div>
  </div>
);